<script>
import axios from 'axios';
import { systemState, STATUS_CACHE_KEY } from '@/helpers/system-status';
import { announcementParts, announcementMessage } from '@/helpers/announcement-text';

/**
 * The live announcements (newest first, each dismissable until it is edited) and,
 * for administrators, the maintenance notice. Rendered under the page title (by
 * PageHeader), or at the top by the layout on a page without one; the layout's
 * SystemBanner keeps the status current.
 */
export default {
  name: 'SystemBannerList',
  data() {
    return { dismissing: {} };
  },
  computed: {
    status() {
      return systemState.status;
    },
    // Still within their window (they appear and expire on their own between refreshes).
    announcements() {
      const now = systemState.now;
      return (this.status?.announcements || [])
        .filter(a => !a.ends_at || this.parseDate(a.ends_at) > now)
        .filter(a => !a.starts_at || this.parseDate(a.starts_at) <= now);
    },
    maintenanceOn() {
      return !!this.status?.maintenance?.enabled && !!this.status?.is_admin;
    },
  },
  methods: {
    parseDate(v) {
      return v ? new Date(String(v).replace(' ', 'T')).getTime() : 0;
    },
    async dismiss(a) {
      this.dismissing = { ...this.dismissing, [a.id]: true };
      // Hidden at once (for every list on the page); the server remembers it until it is edited.
      systemState.status = { ...systemState.status, announcements: systemState.status.announcements.filter(x => x.id !== a.id) };
      try {
        const c = JSON.parse(sessionStorage.getItem(STATUS_CACHE_KEY) || 'null');
        if (c) sessionStorage.setItem(STATUS_CACHE_KEY, JSON.stringify({ ...c, data: systemState.status }));
        await axios.post(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/announcements/${a.id}/dismiss`);
      } catch (e) {
        console.error('Error dismissing the announcement:', e);
      }
    },
    /** The announcement's text and links (an older one's title and link folded in). */
    partsOf(a) {
      return announcementParts(announcementMessage(a));
    },
    levelClass(level) {
      return { success: 'alert-success', warning: 'alert-warning', danger: 'alert-danger' }[level] || 'alert-info';
    },
    levelIcon(level) {
      return { success: 'ri-checkbox-circle-line', warning: 'ri-error-warning-line', danger: 'ri-alarm-warning-line' }[level] || 'ri-notification-3-line';
    },
  },
};
</script>

<template>
  <div v-if="maintenanceOn || announcements.length" class="system-banner">
    <div v-if="maintenanceOn" class="alert alert-danger alert-border-left d-flex align-items-start gap-2 py-2 mb-2" role="alert">
      <i class="ri-tools-line fs-16 mt-1"></i>
      <div class="flex-grow-1">
        <strong>Maintenance mode is on.</strong>
        Everyone except administrators is signed out and sees: <em>"{{ status.maintenance.message }}"</em>
        <router-link to="/settings/system-configuration" class="ms-1 fw-semibold">Turn it off</router-link>
      </div>
    </div>

    <div v-for="a in announcements" :key="a.id + '-' + a.updated_at"
      class="alert alert-border-left d-flex align-items-start gap-2 py-2 mb-2" :class="levelClass(a.level)" role="alert">
      <i :class="levelIcon(a.level)" class="fs-16 mt-1"></i>
      <div class="flex-grow-1 min-w-0 announcement-message">
        <strong class="me-1">Announcement</strong>
        <template v-for="(part, i) in partsOf(a)" :key="i">
          <router-link v-if="part.href && part.internal" :to="part.href" class="fw-semibold">{{ part.text }}</router-link>
          <a v-else-if="part.href" :href="part.href" target="_blank" rel="noopener noreferrer" class="fw-semibold">{{ part.text }} <i class="ri-external-link-line align-middle"></i></a>
          <span v-else>{{ part.text }}</span>
        </template>
      </div>
      <button type="button" class="btn-close btn-sm flex-shrink-0" aria-label="Dismiss" title="Dismiss"
        :disabled="dismissing[a.id]" @click="dismiss(a)"></button>
    </div>
  </div>
</template>

<style scoped>
.system-banner { margin-bottom: 0.75rem; }
.min-w-0 { min-width: 0; }
.announcement-message { white-space: pre-line; font-size: 0.8125rem; }
</style>
